import React, { useEffect, useState } from "react";
import { METHOD_DEFINITIONS } from "../../constants/methods";

const METHOD_LOOKUP = METHOD_DEFINITIONS.reduce((acc, method) => {
  acc[method.id] = method;
  return acc;
}, {});

const normalizeMethod = (method = "") => {
  const key = method.toLowerCase();
  if (key === "linearfit" || key === "polynomialfit") return "linearfit";
  if (key.startsWith("decomposition") || key === "qr") return "decomposition";
  if (key === "iterative linear solvers") return "iterlinsolve";
  return key;
};


export default function RecentLogs({ className = "", onRerun = () => {} }) {
  const [logs, setLogs] = useState([]);
  const [query, setQuery] = useState("");

  const methodColors = {
    gauss: "#2563eb",
    newton: "#16a34a",
    decomposition: "#db2777",
    "decomposition-lu": "#db2777",
    "decomposition-cholesky": "#db2777",
    "decomposition-qr": "#db2777",
    "decomposition-svd": "#db2777",
    qr: "#db2777",
    interpolation: "#9333ea",
    linearfit: "#eab308",
    polynomialfit: "#eab308",
    iterlinsolve: "#38bdf8",
    quadrature: "#0d9488",
    simplex: "#ef4444",
  };

  const formatTitle = (log) => {
    const m = (log.method || '').toLowerCase();
    const input = log.input || {};

    if (m === 'gauss') return 'Gaussian';

    if (m === 'newton') {
      const sub = (input.method || '').toLowerCase();
      if (sub === 'quasi') return 'Newton (Quasi)';
      if (sub === 'gauss') return 'Newton (Gauss)';
      return 'Newton';
    }

    if (m === 'interpolation') {
      const im = (input.method || '').toLowerCase();
      if (im === 'polynomial') return 'Interpolation (Polynomial)';
      if (im === 'linear-spline') return 'Interpolation (Linear Spline)';
      if (im === 'cubic-spline') {
        const st = (input.spline_type || input.splineType || '').toLowerCase();
        // optional addition if desired (Natural/Clamped/Periodic)
        if (st === 'natural') return 'Interpolation (Cubic Spline – Natural)';
        if (st === 'clamped') return 'Interpolation (Cubic Spline – Clamped)';
        if (st === 'periodic') return 'Interpolation (Cubic Spline – Periodic)';
        return 'Interpolation (Cubic Spline)';
      }
      return 'Interpolation';
    }
    if (m === 'linearfit' || m === 'polynomialfit') {
      const deg = input.degree ?? log.result?.degree;
      return typeof deg !== 'undefined'
        ? `Polynomial Fit (degree ${deg})`
        : 'Polynomial Fit';
    }
    if (m === 'decomposition' || m === 'decomposition-qr' || m === 'qr' ||
      m === 'decomposition-lu' || m === 'decomposition-cholesky' || m === 'decomposition-svd') {
      const dm = (input.method || input.decompMethod || m).toLowerCase();
      if (dm.includes('svd') || m === 'decomposition-svd') return 'Decomposition (SVD)';
      if (dm.includes('qr') || m === 'qr' || m === 'decomposition-qr') return 'Decomposition (QR)';
      if (dm.includes('lu') || m === 'decomposition-lu') return 'Decomposition (LU)';
      if (dm.includes('chol') || m === 'decomposition-cholesky') return 'Decomposition (Cholesky)';
      return 'Decomposition';
    }
    if (m === 'iterlinsolve') {
      const lm = (input.method || '').toLowerCase();
      switch (lm) {
        case 'richardson':
          return 'Iterative Solver (Richardson)';
        case 'jacobi':
          return 'Iterative Solver (Jacobi)';
        case 'forward gauss-seidel':
          return 'Iterative Solver (Forward Gauss–Seidel)';
        case 'backward gauss-seidel':
          return 'Iterative Solver (Backward Gauss–Seidel)';
        case 'symmetrical gauss-seidel':
          return 'Iterative Solver (Symmetrical Gauss–Seidel)';
        case 'ssor':
          return 'Iterative Solver (SSOR)';
        case 'cg':
          return 'Iterative Solver (Conjugate Gradient)';
        case 'gmres':
          return 'Iterative Solver (GMRES)';
        default:
          return 'Iterative Solver';
      }
    }
    if (m === 'quadrature') {
      const qm = (input.method || '').toLowerCase();
      if (qm === 'midpoint' || qm === 'box') return 'Quadrature (Midpoint)';
      if (qm === 'trapezoid' || qm === 'trapez') return 'Quadrature (Trapezoid)';
      if (qm === 'simpson') return 'Quadrature (Simpson)';
      return 'Quadrature';
    }
    if (m === 'simplex') {
      return 'Simplex';
    }
    return log.method || 'Unknown';
  };


  useEffect(() => {
    const loadLogs = () => {
      const stored = localStorage.getItem("calcLogs");
      if (!stored) { setLogs([]); return; }
      let arr = JSON.parse(stored);
      let changed = false;
      arr = arr.map((item) => (!item.log && Array.isArray(item.logs)) ? (changed = true, { ...item, log: item.logs }) : item);
      if (changed) localStorage.setItem("calcLogs", JSON.stringify(arr));
      setLogs(arr);
    };

    loadLogs();
    const onStorage = (e) => { if (e.key === "calcLogs") loadLogs(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("calcLogsUpdated", loadLogs);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("calcLogsUpdated", loadLogs);
    };
  }, []);

  // ---------- Helpers ----------
  const fmtMat = (mat = []) =>
    Array.isArray(mat) ? mat.map(r => `[${r.join(', ')}]`).join('\n') : String(mat);
  const fmtVec = (vec = []) =>
    Array.isArray(vec) ? `[${vec.join(', ')}]` : String(vec);
  const fmtPts = (pts = []) =>
    Array.isArray(pts) ? pts.map(p => `[${p[0]}, ${p[1]}]`).join(', ') : String(pts);

  const buildInputTxt = (log) => {
    const m = (log.method || '').toLowerCase();
    const I = log.input || {};
    // Method-specific pretty print
    if (m === 'gauss') {
      return `Matrix A:\n${fmtMat(I.matrix)}\n\nVector b:\n${fmtVec(I.vector)}`;
    }
    if (m === 'newton') {
      return `f(x): ${I.function}
start: ${fmtVec(I.start)}
method: ${I.method}
damping: ${I.damping}  (value: ${I.damping_value})
tolerance: ${I.tolerance}
max_iterations: ${I.max_iterations}`;
    }
    if (m === 'interpolation') {
      return `points: ${fmtPts(I.points)}
method: ${I.method}${I.spline_type ? `  (spline_type: ${I.spline_type})` : ''}`;
    }
    if (m === 'linearfit' || m === 'polynomialfit') {
      return `points: ${fmtPts(I.points)}
degree: ${I.degree}`;
    }
    if (m.startsWith('decomposition')) {
      const shiftTxt =
        I.shift !== undefined && I.shift !== null ? `${I.iterate ? ', ' : '\n'}shift: ${I.shift}` : '';
      return `matrix:\n${fmtMat(I.matrix)}
method: ${I.method}${I.pivoting ? `  (pivoting: ${I.pivoting})` : ''}${I.iterate ? `\niterate: ${I.iterate}, max_iter: ${I.max_iter}, tol: ${I.tolerance}${shiftTxt}` : shiftTxt}`;
    }
    if (m === 'iterlinsolve') {
      const parts = [
        `Matrix A:\n${fmtMat(I.matrix)}`,
        `Vector b: ${fmtVec(I.vector)}`,
        `Method: ${I.method}`,
        `Max iterations: ${I.max_iterations ?? I.maxIterations ?? 'n/a'}`,
        `Tolerance: ${I.tolerance ?? 'n/a'}`,
      ];
      if (typeof I.omega !== 'undefined') parts.push(`omega: ${I.omega}`);
      return parts.join('\n');
    }
    if (m === 'quadrature') {
      return `f(x): ${I.func}
[a,b]: [${I.a}, ${I.b}]
method: ${I.method}${I.use_summed ? `  (composite, n=${I.subintervals})` : ''}`;
    }
    if (m === 'simplex') {
      return `objective: ${fmtVec(I.objective)}
constraints:\n${fmtMat(I.constraints)}
rhs: ${fmtVec(I.rhs)}
relations: ${fmtVec(I.relations)}
maximize: ${I.maximize}`;
    }
    // Fallback
    return I ? JSON.stringify(I, null, 2) : '(no input recorded)';
  };

  const handleExport = (log, format) => {
    const m = (log.method || "").toLowerCase();
    const entries = Array.isArray(log.log) ? log.log : Array.isArray(log.logs) ? log.logs : [];

    const buildTxtBody = () => {
      // Newton (iterative)
      if (m === "newton")
        return entries.map((e, i) => {
          const it = e.iteration ?? i + 1;
          const res = e.residual ?? "n/a";
          const x = Array.isArray(e.x) ? `[${e.x.join(", ")}]` : String(e.x ?? "n/a");
          return `Iteration ${it}\nResidual: ${res}\nX: ${x}\n---`;
        }).join("\n");

      // Gauss (step-by-step)
      if (m === "gauss") {
        const fmtMat = (mat) => (mat || []).map(row => row.map(v => Number(v).toFixed(6)).join("\t")).join("\n");
        const fmtVec = (vec) => (vec || []).map(v => Number(v).toFixed(6)).join(", ");
        const body = entries.map((e, i) => [
          `Step ${i + 1}: ${e.step || ""}`,
          `Matrix A:\n${fmtMat(e.matrix)}`,
          `Vector b:\n${fmtVec(e.vector)}`,
          `---`
        ].join("\n")).join("\n\n");
        const tail = Array.isArray(log.solution) ? `\n\nSolution x: [${log.solution.join(", ")}]` : "";
        return body + tail;
      }

      // QR iterativ
      if (m === "decomposition-qr" || m === "qr") {
        const body = entries.map((e) => [
          `Iteration ${e.iteration ?? "n/a"}`,
          `Q:\n${e.Q_str ?? ""}`,
          ``,
          `R:\n${e.R_str ?? ""}`,
          ``,
          `A:\n${e.A_str ?? ""}`,
          `---`
        ].join("\n")).join("\n\n");
        const extras = [];
        if (log.summary) {
          if (typeof log.summary.converged !== "undefined") extras.push(`Converged: ${log.summary.converged ? "Yes" : "No"}`);
          if (typeof log.summary.final_residual !== "undefined" && log.summary.final_residual !== null) extras.push(`Final residual: ${log.summary.final_residual}`);
          if (Array.isArray(log.summary.eigenvalues)) extras.push(`Eigenvalues: [${log.summary.eigenvalues.join(", ")}]`);
        }
        return extras.length ? `${body}\n\n${extras.join("\n")}` : body;
      }

      if (m === "iterlinsolve") {
        if (!entries.length) return '(no iterations recorded)';
        const body = entries.map((e, i) => {
          const iteration = e.iteration ?? i + 1;
          const residual = typeof e.residual !== 'undefined' ? e.residual : 'n/a';
          const xVal = Array.isArray(e.x) ? `[${e.x.join(", ")}]` : String(e.x ?? 'n/a');
          return `Iteration ${iteration}\nResidual: ${residual}\nX: ${xVal}\n---`;
        }).join("\n\n");
        const tail = Array.isArray(log.solution)
          ? `\n\nSolution: [${log.solution.join(", ")}]`
          : "";
        return body + tail;
      }

      // Snapshots: LU / Cholesky / QR (non-iterative)
      const e0 = entries[0] || {};
      if (e0.kind === "lu") {
        return [
          "LU Decomposition Snapshot",
          "----------------------------------------",
          e0.A_str ? "A:" : null,
          e0.A_str || null,
          e0.P_str ? "\nP:" : null,
          e0.P_str || null,
          "\nL:",
          e0.L_str ?? "(missing L)",
          "\nU:",
          (e0.U_str ?? e0.R_str) ?? "(missing U)",
          ""
        ].filter(Boolean).join("\n");
      }
      if (e0.kind === "cholesky") {
        return [
          "Cholesky Decomposition Snapshot",
          "----------------------------------------",
          e0.A_str ? "A:" : null,
          e0.A_str || null,
          "\nL (A = L·Lᵗ):",
          e0.L_str ?? "(missing L)",
          ""
        ].filter(Boolean).join("\n");
      }
      if (e0.kind === "qr") {
        return [
          "QR Decomposition Snapshot",
          "----------------------------------------",
          e0.A_str ? "A:" : null,
          e0.A_str || null,
          "\nQ:",
          e0.Q_str ?? "(missing Q)",
          "\nR:",
          e0.R_str ?? "(missing R)",
          ""
        ].filter(Boolean).join("\n");
      }

      if (e0.kind === "svd") {
        const singularValues = Array.isArray(e0.singular_values)
          ? `\nSingular values: [${e0.singular_values.join(', ')}]`
          : "";
        return [
          "Singular Value Decomposition Snapshot",
          "----------------------------------------",
          e0.A_str ? "A:" : null,
          e0.A_str || null,
          "\nU:",
          e0.U_str ?? "(missing U)",
          "\nΣ:",
          e0.S_str ?? "(missing Σ)",
          "\nVᵀ:",
          e0.Vt_str ?? "(missing Vᵀ)",
          singularValues,
          ""
        ].filter(Boolean).join("\n");
      }

      // Fallback
      if (!entries.length) return "(no iteration entries)";
      return entries.map((e, i) => `Entry ${i + 1}:\n${JSON.stringify(e, null, 2)}\n---`).join("\n\n");
    };

    const header = `=== USER INPUT ===\n${buildInputTxt(log)}\n\n=== LOG ===\n`;
    const dataStr = (format === "json")
      ? JSON.stringify({
        method: log.method,
        timestamp: log.timestamp,
        input: log.input ?? null,
        result: {
          solution: log.solution ?? undefined,
          residual: log.residual ?? undefined,
          converged: log.converged ?? undefined,
          summary: log.summary ?? undefined,
        },
        log: entries
      }, null, 2)
      : header + buildTxtBody();

    const blob = new Blob([dataStr], {
      type: format === "json" ? "application/json" : "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `log_${log.method}_${log.timestamp}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLogs = logs.filter((log) => {
    const q = query.toLowerCase();
    if (!q) return true;
    const title = formatTitle(log).toLowerCase();
    const dateStr = new Date(log.timestamp).toLocaleString().toLowerCase();
    const isoStr = new Date(log.timestamp).toISOString().toLowerCase();
    return title.includes(q) || dateStr.includes(q) || isoStr.includes(q);
  });

  const getMethodPresentation = (log) => {
    const methodKey = (log.method || "").toLowerCase();
    const normalized = normalizeMethod(methodKey);
    const meta = METHOD_LOOKUP[normalized];
    const color = meta?.color || methodColors[methodKey] || methodColors[normalized] || "#9ca3af";

    let iconNode = null;
    if (meta?.icon) {
      iconNode = React.isValidElement(meta.icon)
        ? React.cloneElement(meta.icon, { size: 42 })
        : meta.icon;
    } else {
      const initial = (formatTitle(log).charAt(0) || "?").toUpperCase();
      iconNode = (
        <span className="text-lg font-semibold text-white">
          {initial}
        </span>
      );
    }

    return { color, iconNode };
  };

  const clearLogs = () => {
    localStorage.removeItem("calcLogs");
    setLogs([]);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 text-white shadow-[0_35px_65px_-45px_rgba(76,29,149,0.8)] backdrop-blur-lg ${className}`}
    >
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-white/10 via-transparent to-transparent" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-white"></h2>
        {logs.length > 0 && (
          <button
            className="rounded-full bg-rose-500/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm transition hover:bg-rose-500"
            onClick={clearLogs}
          >
            Clear history
          </button>
        )}
      </div>

      {logs.length > 0 && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by method or date"
          className="mt-3 w-full rounded-2xl border border-white/15 bg-white/10 px-3 py-2 text-sm text-white placeholder-white/50 shadow-inner shadow-white/5 focus:border-white/40 focus:outline-none"
        />
      )}

      {logs.length === 0 ? (
        <p className="mt-4 text-sm text-white/70">No logs saved yet.</p>
      ) : filteredLogs.length === 0 ? (
        <p className="mt-4 text-sm text-white/70">No logs match your search.</p>
      ) : (
        <div className="mt-6 grid gap-6">
          {filteredLogs.map((log, index) => {
            const { color, iconNode } = getMethodPresentation(log);

            return (
              <div
  key={index}
  className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 text-left text-white shadow-[0_35px_65px_-45px_rgba(76,29,149,0.65)]"
>

                <div className="flex items-start gap-4">
                  <span
                    className="flex h-16 w-16 flex-none items-center justify-center rounded-full text-white shadow-[0_18px_35px_rgba(15,23,42,0.35)]"
                    style={{ backgroundColor: color }}
                  >
                    {iconNode}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-white">{formatTitle(log)}</h3>
                    <p className="mt-1 text-xs text-white/60">
                      {new Date(log.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex-1 rounded-2xl border border-white/10 bg-slate-900/40">
                  <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words p-4 text-[12px] font-mono leading-5 text-white/80">
                    {buildInputTxt(log)}
                  </pre>
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
                  <button
                    className="rounded-full border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/70 transition hover:border-white/40 hover:text-white"
                    onClick={() => handleExport(log, "txt")}
                    aria-label="Export TXT"
                  >
                    TXT
                  </button>
                  <button
                    className="rounded-full border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/70 transition hover:border-white/40 hover:text-white"
                    onClick={() => handleExport(log, "json")}
                    aria-label="Export JSON"
                  >
                    JSON
                  </button>
                  <button
                    className="rounded-full bg-white/20 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-[0_10px_30px_-15px_rgba(15,23,42,0.6)] transition hover:bg-white/30"
                    onClick={() => onRerun(log)}
                    aria-label="Rerun with this input"
                  >
                    Rerun
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
